<template>
  <section class="mt-10">
    <div class="flex items-center justify-between mb-1 gap-3">
      <h2 class="text-xl font-bold tracking-tight text-gray-brand-900">Team Members</h2>
      <PrimaryButton size="lg" @click="addOpen = true">Add team member</PrimaryButton>
    </div>
    <p class="text-sm text-gray-brand-500 mb-4">
      Team members log in with their own phone number and share your customers and orders.
    </p>

    <p v-if="loadError" class="text-sm text-red-600 mb-3">Could not load your team members. Please try again.</p>
    <p v-if="actionError" class="text-sm text-red-600 mb-3">{{ actionError }}</p>

    <div v-if="loading" class="flex justify-center py-8"><BasicSpinner /></div>

    <ul v-else class="flex flex-col gap-3">
      <li
        v-for="member in members"
        :key="member.id"
        :class="['bg-white rounded-lg border p-4 flex flex-wrap items-center justify-between gap-3', cardShadowClasses]"
      >
        <div class="min-w-0">
          <p class="font-semibold text-gray-brand-900 truncate flex items-center gap-2">
            <span class="truncate">{{ member.display_name }}</span>
            <span v-if="member.is_primary" class="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              Primary
            </span>
            <span
              class="rounded px-2 py-0.5 text-xs font-medium"
              :class="member.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'"
            >
              {{ member.is_active ? 'Active' : 'Inactive' }}
            </span>
          </p>
          <p class="text-sm text-gray-brand-500">{{ member.phone_number }}</p>
        </div>
        <SecondaryButton
          v-if="!member.is_primary"
          size="md"
          class="shrink-0"
          :isLoading="togglingId === member.id"
          @click="toggleMember(member)"
        >
          {{ member.is_active ? 'Deactivate' : 'Reactivate' }}
        </SecondaryButton>
      </li>
    </ul>

    <AddTeamMemberModal :is-open="addOpen" @close="addOpen = false" @added="onMemberAdded" />
  </section>
</template>

<script>
import BasicSpinner from '@/components/Molecules/BasicSpinner'
import PrimaryButton from '@/components/Molecules/PrimaryButton'
import SecondaryButton from '@/components/Molecules/SecondaryButton'
import AddTeamMemberModal from '@/components/Reseller/AddTeamMemberModal'
import merchantFetch from '@/utils/merchantFetch'
import { cardShadowClasses } from '@/config'

export default {
  name: 'TeamMembersSection',
  components: { BasicSpinner, PrimaryButton, SecondaryButton, AddTeamMemberModal },
  data() {
    return {
      cardShadowClasses,
      loading: true,
      loadError: false,
      members: [],
      addOpen: false,
      togglingId: null,
      actionError: null
    }
  },
  async mounted() {
    await this.loadMembers()
  },
  methods: {
    async loadMembers() {
      this.loadError = false
      try {
        const res = await merchantFetch('reseller/members', {})
        this.members = res.data || []
      } catch (e) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    onMemberAdded(members) {
      this.addOpen = false
      this.members = members
    },
    async toggleMember(member) {
      this.actionError = null
      this.togglingId = member.id
      try {
        const res = await merchantFetch(`reseller/members/${member.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ is_active: !member.is_active })
        })
        this.members = res.data || []
      } catch (e) {
        this.actionError = 'Could not update this team member. Please try again.'
      } finally {
        this.togglingId = null
      }
    }
  }
}
</script>
